import {
  describeSchedule,
  dmy,
  isoWeekday,
  periodLabel,
  PRIORITIES,
  PRIORITY_LABELS,
  REGULATION_MODE_LABELS,
  REGULATION_MODES,
  SCHEDULE_TYPE_LABELS,
  SCHEDULE_TYPES,
  scheduleSchema,
  upcomingOccurrences,
  WEEKDAY_SHORT,
  WEEKEND_RULE_LABELS,
  WEEKEND_RULES,
  type DaySpec,
  type Priority,
  type RegulationDto,
  type RegulationMode,
  type Schedule,
  type ScheduleType,
  type WeekendRule,
} from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { RichTextEditor } from '../components/rich-text';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { StatusBadge, UserSelect, useUsersList } from '../lib/tasks-ui';
import { Empty, formatDate, Person, useToast } from '../lib/ui';

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
const WEEKDAYS_FULL = ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье'];
const NTH_LABELS: Array<[1 | 2 | 3 | 4 | -1, string]> = [
  [1, 'первый'],
  [2, 'второй'],
  [3, 'третий'],
  [4, 'четвёртый'],
  [-1, 'последний'],
];
const DAY_MODES: Array<[DaySpec['kind'], string]> = [
  ['date', 'Число месяца'],
  ['last', 'Последний день'],
  ['weekday', 'День недели'],
  ['first_workday', 'Первый рабочий день'],
  ['last_workday', 'Последний рабочий день'],
];

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Расписание
// ---------------------------------------------------------------------------

/** Состояние редактора: все поля всех видов, чтобы при переключении вида ничего не терялось. */
interface ScheduleState {
  type: ScheduleType;
  every: number;
  workdaysOnly: boolean;
  weekdays: number[];
  dayKind: DaySpec['kind'];
  day: number;
  nth: 1 | 2 | 3 | 4 | -1;
  weekday: number;
  quarterMonth: number;
  month: number;
  time: string;
  startsOn: string;
  endsOn: string;
  maxRuns: string;
  weekendRule: WeekendRule;
}

function fromSchedule(s: Schedule | null): ScheduleState {
  const day = s && (s.type === 'monthly' || s.type === 'quarterly') ? s.day : null;
  return {
    type: s?.type ?? 'monthly',
    every: s && 'every' in s ? s.every : 1,
    workdaysOnly: s?.type === 'daily' ? s.workdaysOnly : false,
    weekdays: s?.type === 'weekly' ? s.weekdays : [1],
    dayKind: day?.kind ?? 'date',
    day: day?.kind === 'date' ? day.day : s?.type === 'yearly' ? s.day : 1,
    nth: day?.kind === 'weekday' ? day.nth : 1,
    weekday: day?.kind === 'weekday' ? day.weekday : 1,
    quarterMonth: s?.type === 'quarterly' ? s.month : 1,
    month: s?.type === 'yearly' ? s.month : 1,
    time: s?.time ?? '09:00',
    startsOn: s?.startsOn ?? todayLocal(),
    endsOn: s?.endsOn ?? '',
    maxRuns: s?.maxRuns ? String(s.maxRuns) : '',
    weekendRule: s?.weekendRule ?? 'before',
  };
}

function toSchedule(st: ScheduleState): unknown {
  const common = { time: st.time, startsOn: st.startsOn, endsOn: st.endsOn || null, maxRuns: st.maxRuns ? Number(st.maxRuns) : null, weekendRule: st.weekendRule };
  const day: DaySpec =
    st.dayKind === 'date' ? { kind: 'date', day: st.day } : st.dayKind === 'weekday' ? { kind: 'weekday', nth: st.nth, weekday: st.weekday } : { kind: st.dayKind };
  switch (st.type) {
    case 'daily':
      return { type: 'daily', every: st.every, workdaysOnly: st.workdaysOnly, ...common };
    case 'weekly':
      return { type: 'weekly', every: st.every, weekdays: st.weekdays, ...common };
    case 'monthly':
      return { type: 'monthly', every: st.every, day, ...common };
    case 'quarterly':
      return { type: 'quarterly', month: st.quarterMonth, day, ...common };
    case 'yearly':
      return { type: 'yearly', month: st.month, day: st.day, ...common };
  }
}

function DayPicker({ st, set }: { st: ScheduleState; set: (p: Partial<ScheduleState>) => void }) {
  return (
    <div className="inline-pick">
      <select className="select" value={st.dayKind} onChange={(e) => set({ dayKind: e.target.value as DaySpec['kind'] })} aria-label="Какой день">
        {DAY_MODES.map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
      {st.dayKind === 'date' && (
        <span className="inline-field">
          <input className="input" type="number" min={1} max={31} value={st.day} onChange={(e) => set({ day: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })} style={{ width: 80, minWidth: 0 }} aria-label="Число" />
          -го числа
        </span>
      )}
      {st.dayKind === 'weekday' && (
        <>
          <select className="select" value={st.nth} onChange={(e) => set({ nth: Number(e.target.value) as ScheduleState['nth'] })} aria-label="Который">
            {NTH_LABELS.map(([n, l]) => (
              <option key={n} value={n}>
                {l}
              </option>
            ))}
          </select>
          <select className="select" value={st.weekday} onChange={(e) => set({ weekday: Number(e.target.value) })} aria-label="День недели">
            {WEEKDAYS_FULL.map((w, i) => (
              <option key={w} value={i + 1}>
                {w}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

function ScheduleEditor({ st, set }: { st: ScheduleState; set: (p: Partial<ScheduleState>) => void }) {
  const parsed = scheduleSchema.safeParse(toSchedule(st));
  const next = parsed.success ? upcomingOccurrences(parsed.data, todayLocal(), 5) : [];
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="tabs" role="tablist" style={{ marginBottom: 0 }}>
        {SCHEDULE_TYPES.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={st.type === t} className={`tab${st.type === t ? ' active' : ''}`} onClick={() => set({ type: t })}>
            {SCHEDULE_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {st.type === 'daily' && (
        <div className="inline-pick">
          <span className="inline-field">
            Каждые
            <input className="input" type="number" min={1} max={365} value={st.every} onChange={(e) => set({ every: Math.max(1, Number(e.target.value) || 1) })} style={{ width: 80, minWidth: 0 }} aria-label="Дней" />
            дн.
          </span>
          <label className="check">
            <input type="checkbox" checked={st.workdaysOnly} onChange={(e) => set({ workdaysOnly: e.target.checked })} /> только рабочие дни
          </label>
        </div>
      )}

      {st.type === 'weekly' && (
        <div className="inline-pick">
          <span className="inline-field">
            Раз в
            <input className="input" type="number" min={1} max={52} value={st.every} onChange={(e) => set({ every: Math.max(1, Number(e.target.value) || 1) })} style={{ width: 80, minWidth: 0 }} aria-label="Недель" />
            нед., по дням:
          </span>
          {WEEKDAY_SHORT.map((w, i) => {
            const on = st.weekdays.includes(i + 1);
            return (
              <button
                key={w}
                type="button"
                className={`btn btn-sm${on ? ' btn-primary' : ''}`}
                aria-pressed={on}
                onClick={() => set({ weekdays: on ? st.weekdays.filter((x) => x !== i + 1) : [...st.weekdays, i + 1].sort() })}
              >
                {w}
              </button>
            );
          })}
        </div>
      )}

      {st.type === 'monthly' && (
        <div className="inline-pick">
          <span className="inline-field">
            Раз в
            <input className="input" type="number" min={1} max={12} value={st.every} onChange={(e) => set({ every: Math.min(12, Math.max(1, Number(e.target.value) || 1)) })} style={{ width: 80, minWidth: 0 }} aria-label="Месяцев" />
            мес.
          </span>
          <DayPicker st={st} set={set} />
        </div>
      )}

      {st.type === 'quarterly' && (
        <div className="inline-pick">
          <select className="select" value={st.quarterMonth} onChange={(e) => set({ quarterMonth: Number(e.target.value) })} aria-label="Месяц квартала">
            <option value={1}>1-й месяц квартала (янв, апр, июл, окт)</option>
            <option value={2}>2-й месяц квартала (фев, май, авг, ноя)</option>
            <option value={3}>3-й месяц квартала (мар, июн, сен, дек)</option>
          </select>
          <DayPicker st={st} set={set} />
        </div>
      )}

      {st.type === 'yearly' && (
        <div className="inline-pick">
          <input className="input" type="number" min={1} max={31} value={st.day} onChange={(e) => set({ day: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })} style={{ width: 80, minWidth: 0 }} aria-label="Число" />
          <select className="select" value={st.month} onChange={(e) => set({ month: Number(e.target.value) })} aria-label="Месяц">
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="inline-pick">
        <span className="inline-field">
          Создавать в
          <input className="input" type="time" value={st.time} onChange={(e) => set({ time: e.target.value })} style={{ width: 120, minWidth: 0 }} aria-label="Время" />
        </span>
        <span className="inline-field">
          с
          <input className="input" type="date" value={st.startsOn} onChange={(e) => set({ startsOn: e.target.value })} style={{ width: 160, minWidth: 0 }} aria-label="Начало" />
        </span>
        <span className="inline-field">
          по
          <input className="input" type="date" value={st.endsOn} onChange={(e) => set({ endsOn: e.target.value })} style={{ width: 160, minWidth: 0 }} aria-label="Окончание (необязательно)" />
        </span>
        <span className="inline-field">
          не более
          <input className="input" type="number" min={1} value={st.maxRuns} onChange={(e) => set({ maxRuns: e.target.value })} placeholder="∞" style={{ width: 80, minWidth: 0 }} aria-label="Не более раз" />
          раз
        </span>
      </div>
      <label className="field">
        <span>Если день запуска — суббота или воскресенье</span>
        <select className="select" value={st.weekendRule} onChange={(e) => set({ weekendRule: e.target.value as WeekendRule })}>
          {WEEKEND_RULES.map((r) => (
            <option key={r} value={r}>
              {WEEKEND_RULE_LABELS[r]}
            </option>
          ))}
        </select>
      </label>

      <div className={`alert${parsed.success ? ' ok' : ''}`} style={{ display: 'grid', gap: 4 }}>
        {parsed.success ? (
          <>
            <b>{describeSchedule(parsed.data)}</b>
            <span className="faint">Ближайшие запуски:</span>
            {next.length === 0 && <span>по расписанию запусков больше нет</span>}
            {next.map((o) => (
              <span key={o.period}>
                {WEEKDAY_SHORT[isoWeekday(o.runDate) - 1]}, {dmy(o.runDate)} — {periodLabel(parsed.data, o.period)}
                {o.runDate !== o.period && <span className="faint"> (перенесено с {dmy(o.period)})</span>}
              </span>
            ))}
          </>
        ) : (
          <span>{parsed.error.issues[0]?.message}</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Регулярная задача
// ---------------------------------------------------------------------------

function RegulationForm({ reg, onDone }: { reg: RegulationDto | null; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const users = useUsersList();
  const [name, setName] = useState(reg?.name ?? '');
  const [titleTemplate, setTitleTemplate] = useState(reg?.titleTemplate ?? '');
  const [description, setDescription] = useState(reg?.description ?? '');
  const [priority, setPriority] = useState<Priority>(reg?.priority ?? 'normal');
  const [responsibleId, setResponsibleId] = useState(reg?.responsible.id ?? '');
  const [executors, setExecutors] = useState(reg?.executors.map((e) => ({ userId: e.user.id, instruction: e.instruction ?? '' })) ?? []);
  const [adding, setAdding] = useState('');
  const [mode, setMode] = useState<RegulationMode>(reg?.mode ?? 'parallel');
  const [withDue, setWithDue] = useState(reg ? reg.dueDays !== null : true);
  const [dueDays, setDueDays] = useState(reg?.dueDays ?? 3);
  const [dueTime, setDueTime] = useState(reg?.dueTime ?? '18:00');
  const [isActive, setIsActive] = useState(reg?.isActive ?? true);
  const [st, setSt] = useState(() => fromSchedule(reg?.schedule ?? null));
  const set = (p: Partial<ScheduleState>) => setSt((s) => ({ ...s, ...p }));
  const label = (id: string) => users.data?.find((u) => u.id === id) ?? reg?.executors.find((e) => e.user.id === id)?.user;

  const done = (msg: string) => {
    void qc.invalidateQueries({ queryKey: ['regulations'] });
    toast(msg);
  };
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        titleTemplate,
        description: description.trim() ? description : null,
        priority,
        responsibleId,
        executors: executors.map((e) => ({ userId: e.userId, instruction: e.instruction.trim() || null })),
        mode,
        dueDays: withDue ? dueDays : null,
        dueTime,
        schedule: toSchedule(st),
        isActive,
      };
      return reg ? api.patch(`/regulations/${reg.id}`, body) : api.post('/regulations', body);
    },
    onSuccess: () => {
      done(reg ? 'Регулярная задача сохранена' : 'Регулярная задача создана');
      onDone();
    },
  });
  const runNow = useMutation({
    mutationFn: () => api.post<{ taskId: string }>(`/regulations/${reg!.id}/run`, {}),
    onSuccess: (r) => {
      done('Задача создана');
      void navigate(`/tasks/${r.taskId}`);
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/regulations/${reg!.id}`),
    onSuccess: () => {
      done('Регулярная задача удалена');
      onDone();
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const err = save.error instanceof ApiError ? save.error : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const move = (i: number, d: -1 | 1) =>
    setExecutors((l) => {
      const n = [...l];
      [n[i], n[i + d]] = [n[i + d]!, n[i]!];
      return n;
    });

  return (
    <form className="card card-pad" style={{ display: 'grid', gap: 16 }} onSubmit={submit}>
      <h2>{reg ? reg.name : 'Новая регулярная задача'}</h2>
      {err && <div className="alert">{Object.values(err.fields)[0] ?? err.message}</div>}

      <label className="field">
        <span>Название</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoFocus={!reg} placeholder="Например: Еженедельный отчёт по закупкам" aria-invalid={Boolean(err?.fields.name)} />
      </label>
      <label className="field">
        <span>Название каждой задачи</span>
        <input className="input" value={titleTemplate} onChange={(e) => setTitleTemplate(e.target.value)} maxLength={300} placeholder="{{name}} — {{period}}" />
        <span className="faint">
          {'{{name}}'} — название регулярной задачи, {'{{period}}'} — период («октябрь 2026», «IV квартал 2026»), {'{{date}}'} — дата запуска. Пусто — «название — период».
        </span>
      </label>
      <div className="field">
        <span>Что нужно сделать</span>
        <RichTextEditor value={description} onChange={setDescription} placeholder="Описание работы: что подготовить, куда положить результат" />
      </div>
      <label className="field" style={{ maxWidth: 260 }}>
        <span>Приоритет</span>
        <select className="select" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABELS[p]}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="field" style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 10 }}>
        <legend style={{ fontWeight: 700, marginBottom: 6 }}>Маршрут: исполнители → ответственный</legend>
        <div className="tabs" role="tablist" style={{ marginBottom: 0 }}>
          {REGULATION_MODES.map((m) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m} className={`tab${mode === m ? ' active' : ''}`} onClick={() => setMode(m)}>
              {REGULATION_MODE_LABELS[m]}
            </button>
          ))}
        </div>
        <span className="faint">
          {mode === 'parallel' ? 'Задача приходит всем исполнителям сразу.' : 'Задача приходит следующему исполнителю, когда ответственный принял результат предыдущего.'}
        </span>
        {executors.length === 0 && <span className="faint">Добавьте исполнителей</span>}
        {executors.map((e, i) => {
          const u = label(e.userId);
          return (
            <div key={e.userId} className="step-edit-row wrap">
              <span className="faint" style={{ width: 22 }}>
                {i + 1}.
              </span>
              <div style={{ minWidth: 200 }}>
                <Person name={u?.fullName ?? '…'} sub={u?.position} />
              </div>
              <input
                className="input"
                style={{ flex: 1, minWidth: 220 }}
                value={e.instruction}
                maxLength={2000}
                placeholder="Что именно делает этот исполнитель (необязательно)"
                onChange={(ev) => setExecutors((l) => l.map((x, j) => (j === i ? { ...x, instruction: ev.target.value } : x)))}
              />
              <button type="button" className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Выше">
                ↑
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={i === executors.length - 1} onClick={() => move(i, 1)} aria-label="Ниже">
                ↓
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExecutors((l) => l.filter((_, j) => j !== i))} aria-label="Убрать">
                ×
              </button>
            </div>
          );
        })}
        <div className="step-edit-row">
          <UserSelect value={adding} onChange={setAdding} exclude={executors.map((e) => e.userId)} placeholder="Добавить исполнителя" />
          <button
            type="button"
            className="btn"
            disabled={!adding}
            onClick={() => {
              setExecutors((l) => [...l, { userId: adding, instruction: '' }]);
              setAdding('');
            }}
          >
            Добавить
          </button>
        </div>
        <label className="field">
          <span>Ответственный — принимает результат каждого исполнителя или возвращает на доработку</span>
          <UserSelect value={responsibleId} onChange={setResponsibleId} placeholder="Выберите ответственного" invalid={Boolean(err?.fields.responsibleId)} />
        </label>
      </fieldset>

      <div className="field">
        <span>Срок исполнителям</span>
        <div className="inline-pick">
          <label className="check">
            <input type="checkbox" checked={withDue} onChange={(e) => setWithDue(e.target.checked)} /> со сроком
          </label>
          {withDue && (
            <>
              <span className="inline-field">
                через
                <input className="input" type="number" min={0} max={365} value={dueDays} onChange={(e) => setDueDays(Math.max(0, Number(e.target.value) || 0))} style={{ width: 80, minWidth: 0 }} aria-label="Дней" />
                дн. после запуска, к
                <input className="input" type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} style={{ width: 120, minWidth: 0 }} aria-label="Время срока" />
              </span>
              <span className="faint">0 — в день запуска; срок на выходной переносится так же, как запуск</span>
            </>
          )}
        </div>
      </div>

      <div className="field">
        <span>Расписание</span>
        <ScheduleEditor st={st} set={set} />
      </div>

      <label className="check">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Активна — задачи создаются по расписанию
      </label>
      {reg && <p className="faint">Изменения действуют на следующие запуски; уже созданные задачи не меняются.</p>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={save.isPending || !name.trim() || !responsibleId || executors.length === 0}>
          Сохранить
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>
          Отмена
        </button>
        {reg?.nextPeriod && (
          <button
            type="button"
            className="btn"
            disabled={runNow.isPending}
            onClick={() => confirm(`Создать задачу за ${periodLabel(reg.schedule, reg.nextPeriod!)} сейчас, не дожидаясь расписания?`) && runNow.mutate()}
          >
            Запустить сейчас
          </button>
        )}
        {reg && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginLeft: 'auto', color: 'var(--danger)' }}
            disabled={remove.isPending}
            onClick={() => confirm(`Удалить регулярную задачу «${reg.name}»?`) && remove.mutate()}
          >
            Удалить
          </button>
        )}
      </div>
      {reg && <Runs id={reg.id} />}
    </form>
  );
}

/** История запусков: задачи по периодам. */
function Runs({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['regulations', id], queryFn: () => api.get<RegulationDto>(`/regulations/${id}`) });
  const runs = q.data?.runs ?? [];
  return (
    <div className="field">
      <span>Запуски</span>
      {runs.length === 0 ? (
        <span className="faint">Запусков ещё не было</span>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Период</th>
                <th>Задача</th>
                <th>Статус</th>
                <th>Принято</th>
                <th>Срок</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.taskId}>
                  <td>{q.data ? periodLabel(q.data.schedule, r.period) : r.period}</td>
                  <td>
                    <Link to={`/tasks/${r.taskId}`}>№{r.number}</Link>
                  </td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td>
                    {r.accepted} из {r.total}
                  </td>
                  <td>{formatDate(r.dueAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Карточка для сотрудника без права настройки: что, когда и кто. */
function RegulationView({ reg, onDone }: { reg: RegulationDto; onDone: () => void }) {
  return (
    <div className="card card-pad" style={{ display: 'grid', gap: 12 }}>
      <h2>{reg.name}</h2>
      <p>{describeSchedule(reg.schedule)}</p>
      <p>
        <b>{REGULATION_MODE_LABELS[reg.mode]}:</b> {reg.executors.map((e) => e.user.fullName).join(', ')} → принимает {reg.responsible.fullName}
      </p>
      <Runs id={reg.id} />
      <div>
        <button className="btn btn-ghost" onClick={onDone}>
          Закрыть
        </button>
      </div>
    </div>
  );
}

/** Автозадачи (регулярные задачи): поручения, которые создаются сами по расписанию. */
export function RegulationsPage() {
  const { can } = useAuth();
  const manage = can('regulations.manage');
  const [params, setParams] = useSearchParams();
  const list = useQuery({ queryKey: ['regulations'], queryFn: () => api.get<RegulationDto[]>('/regulations') });
  const [editing, setEditing] = useState<RegulationDto | 'new' | null>(params.get('new') && manage ? 'new' : null);
  const close = () => {
    setEditing(null);
    if (params.get('new')) setParams({});
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Автозадачи</h1>
          <p className="muted">
            Регулярные поручения: каждый понедельник, 1-го числа, в конце квартала. Задача приходит исполнителям сама, ответственный принимает результат.
          </p>
        </div>
        {manage && editing === null && (
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={18} /> Новая регулярная задача
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gap: 14, maxWidth: 920 }}>
        {editing !== null &&
          (manage ? (
            <RegulationForm key={editing === 'new' ? 'new' : editing.id} reg={editing === 'new' ? null : editing} onDone={close} />
          ) : (
            editing !== 'new' && <RegulationView reg={editing} onDone={close} />
          ))}
        {editing === null && (
          <details className="card card-pad" open={list.data?.length === 0}>
            <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Как это работает</summary>
            <ol style={{ margin: '10px 0 0', paddingLeft: 20, display: 'grid', gap: 6 }}>
              {manage ? (
                <>
                  <li>Нажмите «Новая регулярная задача» (или «Что создаём?» → «Регулярные задачи»).</li>
                  <li>Укажите название и описание работы, выберите исполнителей и ответственного. Исполнители работают одновременно или по очереди.</li>
                  <li>Задайте расписание: ежедневно, по дням недели, 1-го числа месяца, в последний рабочий день квартала и т. п. Под формой видны ближайшие запуски.</li>
                  <li>Сохраните. В назначенное время задача появится у исполнителей в «Ждут меня» сама.</li>
                  <li>Исполнитель отправляет результат на проверку, ответственный принимает его или возвращает на доработку.</li>
                </>
              ) : (
                <>
                  <li>Здесь видны регулярные задачи, где вы исполнитель или ответственный.</li>
                  <li>Задача приходит в «Ждут меня» сама, по расписанию.</li>
                  <li>Исполнитель отправляет результат на проверку, ответственный принимает его или возвращает на доработку.</li>
                  <li>Создавать регулярные задачи может сотрудник с правом «Регулярные задачи» — оно выдаётся администратором в карточке сотрудника.</li>
                </>
              )}
            </ol>
          </details>
        )}
        {list.data?.length === 0 && editing === null && (
          <div className="card">
            <Empty emoji="🗓️" title="Регулярных задач пока нет">
              {manage ? 'Например: «Отчёт по закупкам» — каждый понедельник, исполнители — закупщики, принимает руководитель' : 'Здесь появятся регулярные задачи, где вы исполнитель или ответственный'}
            </Empty>
          </div>
        )}
        {list.data?.map((r) => (
          <button key={r.id} className="card card-pad route-card" style={{ textAlign: 'left', opacity: r.isActive ? 1 : 0.65 }} onClick={() => setEditing(r)}>
            <div className="section-head">
              <b>{r.name}</b>
              {r.isActive ? <span className="faint">следующий запуск: {formatDate(r.nextRunAt)}</span> : <span className="badge">выключена</span>}
            </div>
            <span className="faint">{describeSchedule(r.schedule)}</span>
            <div className="chain">
              {r.executors.map((e) => (
                <span key={e.user.id} className="chip">
                  {e.user.fullName}
                </span>
              ))}
              <span className="faint">→</span>
              <span className="chip">✓ {r.responsible.fullName}</span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
